"use client";

import { useMemo, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  IconApi,
  IconChevronDown,
  IconChevronLeft,
  IconSettingsCode,
  IconX,
} from "@tabler/icons-react";
import Input from "../../../components/Input";
import Section from "../../../components/common/Section";
import {
  useChannelConnectionMutation,
  useFetchChannelDetails,
} from "../../../apis/integrationApis";
import toast from "react-hot-toast";
import CommonModal from "../../../components/common/CommonModal";
import { useLanguage } from "../../../context/LanguageContext";
import WebhookModal from "./WebhookModal";
const url = process.env.REACT_APP_BASE_URL;

type CredentialField = {
  key: string;
  label: string;
  placeholder?: string;
  required: boolean;
  type?: string;
};

const partnerFields: Record<string, CredentialField[]> = {
  Aramex: [
    { key: "AccountNumber", label: "Account Number", required: true },
    { key: "AccountPin", label: "Account Pin", required: true },
    { key: "UserName", label: "Username", required: true },
    { key: "Password", label: "Password", required: true },
    { key: "AccountEntity", label: "Account Entity", required: true },
    {
      key: "AccountCountryCode",
      label: "Account Country Code",
      required: true,
    },
    { key: "PaymentType", label: "Payment Type", required: true },
    { key: "ProductGroup", label: "Product Group", required: true },
    { key: "ProductType", label: "Product Type", required: true },
  ],
  SMSA: [
    { key: "username", label: "Username", required: true },
    { key: "password", label: "Password", required: true },
  ],
  Naqel: [
    { key: "userName", label: "Username", required: true },
    { key: "password", label: "Password", required: true, type: "password" },
    {
      key: "customerCode",
      label: "Customer Code",
      required: true,
      placeholder: "e.g. NL123456",
    },
    {
      key: "branchCode",
      label: "Branch Code",
      required: true,
      placeholder: "e.g. NL567899",
    },
  ],
  "Naqel Express": [
    { key: "userName", label: "Username", required: true },
    { key: "password", label: "Password", required: true, type: "password" },
    {
      key: "customerCode",
      label: "Customer Code",
      required: true,
      placeholder: "e.g. NL122212",
    },
    {
      key: "branchCode",
      label: "Branch Code",
      required: true,
      placeholder: "e.g. NL312542",
    },
  ],
  "J&T Express": [
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "Enter customer code" },
    { key: "apiAccount", label: "API Account", required: true, placeholder: "Enter API account" },
    { key: "privateKey", label: "Private Key", required: true, type: "password", placeholder: "Enter private key" },
  ],
  "Zid": [
    { key: "clientId", label: "Client ID", required: true, placeholder: "e.g. 1234" },
    { key: "clientSecret", label: "Client Secret", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "appId", label: "App ID", required: true, placeholder: "e.g. 1234" },
  ],
  "Shopify": [
    { key: "shopDomain", label: "Shop Domain", required: true, placeholder: "e.g. myshop.myshopify.com" },
    { key: "appId", label: "API Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "secretKey", label: "API Secret Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "accessToken", label: "Access Token", required: true, placeholder: "e.g. shpat_***********" },
    { key: "apiVersion", label: "API Version", required: true, placeholder: "e.g. 2024-07" },
  ],
  "Shoapify": [
    { key: "shopDomain", label: "Shop Domain", required: true, placeholder: "e.g. myshop.myshopify.com" },
    { key: "appId", label: "API Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "secretKey", label: "API Secret Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "accessToken", label: "Access Token", required: true, placeholder: "e.g. shpat_***********" },
    { key: "apiVersion", label: "API Version", required: true, placeholder: "e.g. 2024-07" },
  ],
};

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

interface Props {
  channelId: string;
}

const CredentialsModal = ({ channelId }: Props) => {
  const { t } = useLanguage();
  // const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [webhookUrl, setWebhookUrl] = useState<string>("");

  // const { data: channelData, isFetched } = useFetchChannelDetails(id ?? "");
  const { data: channelData, isFetched } = useFetchChannelDetails(channelId);
  const { mutate, isPending: isConnectionPending } =
    useChannelConnectionMutation(
      channelData?.requestedId ?? "",
      channelData?.companyName,
      channelData?.installedBy,
    );

  const defaultValues = useMemo(() => {
    const partner = channelData?.companyName;
    const fields = partnerFields[partner] || [];
    const defaults: Record<string, string> = {};
    fields.forEach((field) => {
      defaults[field.key] = "";
    });
    return defaults;
  }, [channelData]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<Record<string, string>>({
    mode: "onChange",
    defaultValues,
  });

  const onSubmit: SubmitHandler<Record<string, string>> = (data) => {
    mutate(
      { credentials: data, connectionId: channelData?.requestedId },
      {
        onSuccess: (res: any) => {
          toast.success("Connected successfully!");
          reset();
          if (res?.webhookUrl) {
            setWebhookUrl(res?.webhookUrl);
            setIsOpen(true);
          }
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to connect");
        },
      },
    );
  };

  if (!isFetched || !channelData) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-lg text-gray-600">{t("Loading credentials...")}</div>
      </div>
    );
  }

  const fieldsToRender =
    channelData?.companyName && partnerFields[channelData.companyName]
      ? partnerFields[channelData.companyName]
      : channelData?.credentialKeys || [];

  const isConnected = channelData.connectionStatus === "connected";
  const isFormValid = isValid && isDirty;

  return (
    <div className="w-full mx-auto text-[#1E293B] space-y-6">
      <form id="credentials-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img
              src={
                channelData?.companyLogo ||
                "https://placehold.co/40x40/f1f1f1/333?text=Logo"
              }
              className="w-10 h-10 rounded object-contain border border-gray-200"
              alt={channelData?.companyName}
            />
            <h1 className="text-xl font-medium">{channelData?.companyName}</h1>
          </div>

          {isConnected ? (
            <button
              disabled
              className="px-6 py-2 rounded-md bg-green-500 text-white opacity-80 cursor-not-allowed"
            >
              Connected
            </button>
          ) : (
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={!isFormValid}
              className={`px-6 py-2 rounded-md text-sm font-medium ${
                isFormValid
                  ? "bg-yellow-400 text-black hover:bg-yellow-300"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Connect
            </button>
          )}
        </div> */}

        <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
          <SectionHeader
            icon={IconSettingsCode}
            title="API Access Information"
          />

          <div className="grid gap-4">
            {fieldsToRender?.map((field: CredentialField) => {
              const isDropdown = [
                "PaymentType",
                "ProductGroup",
                "ProductType",
              ].includes(field.key);

              if (isDropdown) {
                // Dropdown options define karna
                const options =
                  {
                    PaymentType: [
                      { value: "P", label: "P" },
                      { value: "C", label: "C" },
                      { value: "3", label: "3" },
                    ],
                    ProductGroup: [{ value: "DOM", label: "DOM" }],
                    ProductType: [{ value: "CDS", label: "CDS" }],
                  }[field.key] ?? [];

                return (
                  <div
                    key={field.key}
                    className="flex items-center justify-between"
                  >
                    <label className="text-sm font-medium w-full">
                      {t(field.label)}
                    </label>
                    <div className="relative w-full">
                      <select
                        {...register(field.key, { required: field.required })}
                        className="w-full px-4 py-3 pr-10 text-sm rounded-xl border border-[#D8D8D8] bg-white shadow-sm appearance-none outline-none transition-all focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/30"
                      >
                        <option value="">{t("Select")} {t(field.label)}</option>
                        {options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>

                      <IconChevronDown
                        size={18}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                      />
                    </div>
                    {errors[field.key] && (
                      <span className="text-red-500 text-sm mt-1">
                        {errors[field.key]?.message}
                      </span>
                    )}
                  </div>
                );
              } else {
                // Normal text input for all other fields
                return (
                  <Input
                    key={field.key}
                    className="flex items-center justify-between"
                    labelClass="w-full"
                    name={field.key}
                    type={field.type || "text"}
                    title={t(field.label)}
                    placeholder={field.placeholder ? t(field.placeholder) : (t("Enter") + " " + t(field.label))}
                    register={register}
                    validation={{
                      required: field.required
                        ? t(`${field.label} is required`)
                        : false,
                    }}
                    error={errors[field.key]}
                    other={{
                      className:
                        "px-4 py-3 text-sm w-full rounded-xl outline-none bg-white border border-[#D8D8D8] focus:border-blue-500 transition",
                    }}
                  />
                );
              }
            })}
          </div>
        </div>
      </form>

      {/* <CommonModal
        title={"{t("Webhook URL")}"}
        open={isOpen}
        closeButtonText="Cancel"
        onClose={() => setIsOpen(false)}
        actionButtonText={""}
        actionButtonOnClick={() => setIsOpen(false)}
        actionButtonProps={{ disabled: isConnectionPending }}
      >
        <WebhookModal webhookUrl={webhookUrl} tab={channelData?.companyName} />
      </CommonModal> */}

      {isOpen && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-[#212121]">
              Webhook URL
            </h2>
            <button
              onClick={() => setIsOpen(false)}
              disabled={isConnectionPending}
              className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
            >
              <IconX size={20} />
            </button>
          </div>

          <WebhookModal
            webhookUrl={webhookUrl}
            tab={channelData?.companyName}
          />
        </div>
      )}
    </div>
  );
};

export default CredentialsModal;
